import React from 'react';
import { Box, Link as MuiLink, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { tokens } from '../../../theme/tokens';
import { EmptyState, FormDialog } from '../../ui';
import { Pill } from './primitives';
import type { StoreOverrideRow } from './productDetailTypes';
import { formatDate, formatDateTime, money } from './productDetailFormat';

interface Props {
  open: boolean;
  onClose: () => void;
  productName: string;
  rows: StoreOverrideRow[];
  /** Every override of the product; the list holds the newest of them. */
  total: number;
  currency: string;
}

const priceWindow = (row: StoreOverrideRow): string => {
  if (!row.overridePriceStartAt && !row.overridePriceEndAt) return 'no end date';
  return `${row.overridePriceStartAt ? formatDate(row.overridePriceStartAt) : 'now'} → ${row.overridePriceEndAt ? formatDate(row.overridePriceEndAt) : 'open'}`;
};

/**
 * Read-only list of the stores that override one product (MOT-387). Stores own
 * their overrides, so this only says who overrides what; each row links to the
 * store's admin page.
 */
export const StoreOverridesDialog: React.FC<Props> = ({ open, onClose, productName, rows, total, currency }) => (
  <FormDialog
    open={open}
    title={`Store overrides · ${productName}`}
    onClose={onClose}
    onSubmit={onClose}
    submitLabel="Close"
    hideCancel
    maxWidth="md"
  >
    <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, mb: 1.5 }}>
      {total > rows.length
        ? `Newest ${rows.length} of ${total} stores. Stores edit their own overrides; nothing here changes them.`
        : `${total} store${total === 1 ? '' : 's'} override this product. Stores edit their own overrides; nothing here changes them.`}
    </Typography>
    {rows.length === 0 ? (
      <EmptyState title="No store overrides" description="No store has overridden this product." />
    ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {rows.map((row) => (
          <Box
            key={row.id}
            sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1.5, py: 1.25, minHeight: 48, borderTop: `1px solid ${tokens.line.faint}` }}
          >
            <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
              {row.organizationId ? (
                <MuiLink component={RouterLink} to={`/app/admin/stores/${encodeURIComponent(row.organizationId)}`} underline="hover" sx={{ fontSize: 13, fontWeight: 600 }}>
                  {row.organizationName || row.organizationId}
                </MuiLink>
              ) : (
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Unknown store</Typography>
              )}
              <Typography sx={{ fontSize: 11.5, color: tokens.ink.faint }}>
                {row.updatedAt ? `Updated ${formatDateTime(row.updatedAt)}` : 'Update date unknown'}
              </Typography>
            </Box>
            <Box sx={{ flex: '2 1 280px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                {row.fields.length ? row.fields.map((f) => <Pill key={f} tone="neutral" dot={false}>{f}</Pill>) : (
                  <Typography component="span" sx={{ fontSize: 12.5, color: tokens.ink.faint }}>Nothing overridden (empty record)</Typography>
                )}
              </Stack>
              {row.overridePrice != null && (
                <Typography sx={{ fontSize: 12.5, color: tokens.ink.body, fontVariantNumeric: 'tabular-nums' }}>
                  {money(row.overridePrice, currency)} · {priceWindow(row)}
                  {row.belowMap ? ' · below MAP' : ''}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    )}
  </FormDialog>
);
